import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";

import { PaymentOtherChoiceIcon, PaymentPopUpCloseIcon } from "../../../../assets/icons/MUI/PaymentIcons";
import { useCountryListing } from "../../../../api/queries/network/useCountryListing";
import { formatImgUrl } from "../../../../utils/cardFormating";
import APP_ROUTES from "../../../../routers/routeConstants";

import { CountryLabel, SelectCountryPopUpProps } from "../../payment.types";

const SelectCountryPopUp = ({ onClose, open }: SelectCountryPopUpProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { countries = [], isCountriesDataLoading } = useCountryListing();

  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const COUNTRIES_LIST: CountryLabel[] = countries.filter((country: any) => ["eg"].includes(country.shortCode));

  const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCountry(event.target.value);
  };

  const handleContinue = () => {
    if (!selectedCountry) return;
    if (selectedCountry === "eg") {
      navigate(`${APP_ROUTES.CREATE_PAYMENT_LINK}?country=${selectedCountry}`);
    } else {
      navigate(`${APP_ROUTES.CREATE_PAYMENT_ACCOUNT}?country=${selectedCountry}`);
    }
  };

  const OTHER: CountryLabel = {
    label: t("pages.payment.overview.selectCountryPopUp.other"),
    url: "",
    shortCode: "other",
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          width: "100%",
          maxWidth: "640px",
          borderRadius: "12px",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          padding: "24px 0",
          borderBottom: "1px solid #E4E7EC",
        }}
      >
        <Typography
          component="h3"
          sx={{
            color: "#101828",
            textAlign: "center",
            fontFamily: "Montserrat",
            fontSize: "18px",
            fontWeight: 600,
          }}
        >
          {t("pages.payment.overview.selectCountryPopUp.selectCountry")}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: "16px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <PaymentPopUpCloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "12px",
          padding: "16px",
        }}
      >
        {isCountriesDataLoading ? (
          <Box sx={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress size={24} />
          </Box>
        ) : COUNTRIES_LIST.length > 0 ? (
          <RadioGroup value={selectedCountry} onChange={handleSelectionChange}>
            {COUNTRIES_LIST.map((country) => (
              <CountryRadioChoice key={country.shortCode} country={country} />
            ))}
            <CountryRadioChoice key={OTHER.shortCode} country={OTHER} />
          </RadioGroup>
        ) : (
          <Typography sx={{ textAlign: "center", color: "gray" }}>
            {t("pages.payment.overview.selectCountryPopUp.noCountries")}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          columnGap: "12px",
          padding: "12px 16px",
          borderTop: "1px solid #EAECF0",
        }}
      >
        <Button variant="outlined-md" onClick={onClose}>
          {t("pages.payment.overview.selectCountryPopUp.cancel")}
        </Button>
        <Button variant="primary-md" onClick={handleContinue} disabled={!selectedCountry}>
          {t("pages.payment.overview.selectCountryPopUp.continue")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default SelectCountryPopUp;

const CountryRadioChoice = ({ country }: { country: CountryLabel }) => {
  const { label, shortCode, url } = country;
  const COUNTRY_IMG = formatImgUrl(url);

  return (
    <FormControlLabel
      value={shortCode}
      control={<Radio />}
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {shortCode === "other" ? (
            <PaymentOtherChoiceIcon />
          ) : (
            <img src={COUNTRY_IMG} alt={label} width={24} height={24} style={{ borderRadius: "200px" }} />
          )}
          <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>{label}</Typography>
        </Box>
      }
      sx={{
        padding: "10px 8px",
        width: "100%",
        justifyContent: "space-between",
        flexDirection: "row-reverse",
        margin: 0,
      }}
    />
  );
};
